/**
 * AIScribePanel — record or upload audio, send to AI Scribe, display results.
 *
 * Renders as a collapsible panel inside the SOAP Diagnosis Pad.
 * On success, calls onSoapFilled() so the parent can populate SOAP fields.
 */
import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudioRecorder } from "../hooks/useAudioRecorder";
import {
  runAiScribeAsync,
  runAiScribe,
  type AiScribeResponse,
  type AiScribeMedication,
  type ScribeJobStatus,
} from "../services/ai-scribe.service";
import AIScribeConsentModal from "./AIScribeConsentModal";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { LockedFeatureEmptyState, TrialActiveBanner } from "./billing";
import { FeatureGateChip } from "./billing/FeatureGateChip";
import { UpgradePromptModal } from "./billing/UpgradePromptModal";
import { apiRequest } from "../services/api.client";
import { reportChunkLoadFailure } from "../utils/flow-telemetry";

// ── Props ───────────────────────────────────────────────────────────────────

interface AIScribePanelProps {
  visitId: string;
  patientId?: string;
  /** Called when AI Scribe returns a SOAP note — parent fills SOAP fields */
  onSoapFilled?: (soap: AiScribeResponse["soap_note"]) => void;
  /** Called when AI Scribe returns a prescription — parent can navigate to Rx */
  onPrescriptionReady?: (rx: AiScribeResponse["prescription_document"]) => void;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const ACCEPTED = ".mp3,.wav,.m4a,.ogg,.webm,.flac,.aac";

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const FREQ_LABEL: Record<string, string> = {
  OD: "Once daily",
  BD: "Twice daily",
  TDS: "Three times daily",
  QID: "Four times daily",
  SOS: "As needed",
  HS: "At bedtime",
};

// ── Component ───────────────────────────────────────────────────────────────

const AIScribePanel: React.FC<AIScribePanelProps> = ({
  visitId,
  patientId,
  onSoapFilled,
  onPrescriptionReady,
}) => {
  const navigate = useNavigate();
  const { isRecording, duration, startRecording, stopRecording } =
    useAudioRecorder();

  const [status, setStatus] = useState<
    "idle" | "recording" | "processing" | "done" | "error"
  >("idle");
  const [result, setResult] = useState<AiScribeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Feature gating
  const { getGateState, getFlag, isAdmin } = useFeatureFlags();
  const gateState = getGateState("ai_scribe");
  const aiScribeFlag = getFlag("ai_scribe");
  const isLocked = gateState === "locked" || gateState === "expired";

  const handleUpgradeClick = () => navigate("/settings", { state: { section: "finance" } });
  const handleNotifyAdmin = () => {
    window.location.href = "mailto:?subject=Upgrade%20Kaagazy%20EMR%20Subscription&body=Hi%2C%20please%20upgrade%20our%20clinic%20subscription%20to%20continue%20using%20AI%20Scribe.";
  };

  /** Convert async ScribeJobStatus → AiScribeResponse for the result UI. */
  const jobToResponse = (job: ScribeJobStatus): AiScribeResponse => ({
    soap_note: job.soap_note ?? { chief_complaint: "", history_of_present_illness: "", review_of_systems: null, physical_examination: null, assessment: "", plan: "" },
    prescription_document: job.prescription ?? { diagnosis_label: "", medications: [], investigations: [], advice: null, follow_up: null },
    transcript: job.transcript ?? undefined,
    meta: { model: "gemini", processing_time_s: 0, audio_size_mb: 0, s3_key: null },
  });

  const processAudio = useCallback(
    async (audioBlob: Blob, filename?: string) => {
      setStatus("processing");
      setError(null);
      try {
        const { compressAudio, needsCompression } = await import("../utils/audioCompress");
        let finalAudio: Blob = audioBlob;
        if (needsCompression(audioBlob)) {
          try {
            finalAudio = await compressAudio(audioBlob);
          } catch { finalAudio = audioBlob; }
        }

        // Try async flow (S3 upload → Worker Lambda) first.
        // Falls back to legacy sync if the /scribe/jobs endpoint isn't available.
        let res: AiScribeResponse;
        try {
          const job = await runAiScribeAsync(visitId, patientId || "", finalAudio);
          if (job.status === "failed") throw new Error(job.error || "AI Scribe processing failed");
          res = jobToResponse(job);
        } catch (asyncErr: any) {
          // Fallback to legacy sync endpoint if async flow not available (e.g. worker Lambda not deployed)
          if (asyncErr?.message?.includes("404") || asyncErr?.message?.includes("not found")) {
            res = await runAiScribe(visitId, finalAudio, filename?.replace(/\.\w+$/, ".webm") || "recording.webm");
          } else {
            throw asyncErr;
          }
        }

        setResult(res);
        setStatus("done");
        if (onSoapFilled && res.soap_note) onSoapFilled(res.soap_note);
        if (onPrescriptionReady && res.prescription_document)
          onPrescriptionReady(res.prescription_document);
      } catch (e: any) {
        // audioCompress is the one lazily loaded chunk on this path.
        reportChunkLoadFailure(e);
        const msg = e?.message || "AI Scribe failed. Please try again.";
        if (msg.includes("ai_unavailable")) {
          setError("AI Scribe temporarily unavailable. Please try again later.");
        } else if (msg.includes("feature_not_enabled")) {
          setError("AI Scribe is not enabled for your clinic. Contact admin to upgrade.");
        } else {
          setError(msg);
        }
        setStatus("error");
      }
    },
    [visitId, patientId, onSoapFilled, onPrescriptionReady]
  );

  const requestConsent = (action: () => void) => {
    pendingActionRef.current = action;
    setConsentOpen(true);
  };

  const handleConsentAccepted = async () => {
    setConsentOpen(false);
    // Persist consent to DB so the backend AI Scribe endpoints pass the DPDPA check
    try {
      await apiRequest(`/visits/${visitId}/ai-consent`, { method: "POST" });
    } catch (e) {
      console.warn("Failed to record AI consent", e);
    }
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  };

  const handleConsentDeclined = () => {
    setConsentOpen(false);
    pendingActionRef.current = null;
  };

  const handleRecordToggle = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (blob && blob.size > 0) {
        await processAudio(blob, "recording.webm");
      }
    } else {
      requestConsent(() => {
        setStatus("recording");
        startRecording();
      });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const captured = file;
    e.target.value = "";
    requestConsent(() => {
      processAudio(captured, captured.name);
    });
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setStatus("idle");
  };

  if (!visitId) return null;

  return (
    <div className="mb-4">
      <AIScribeConsentModal
        isOpen={consentOpen}
        onConsent={handleConsentAccepted}
        onDecline={handleConsentDeclined}
      />
      {/* Toggle bar */}
      <button
        type="button"
        onClick={() => {
          if (isLocked) { setShowUpgradeModal(true); return; }
          setIsOpen(!isOpen);
        }}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl hover:from-violet-100 hover:to-purple-100 transition-all"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🎙️</span>
          <span className="font-semibold text-violet-800 text-sm">
            AI Scribe
          </span>
          <span className="text-xs text-violet-500 hidden sm:inline">
            Record consultation → auto-fill SOAP + Prescription
          </span>
          {/* S5 — Feature Gate Chip */}
          <FeatureGateChip
            state={gateState === "active" ? "hidden" : gateState}
            daysLeft={aiScribeFlag?.days_left}
            onClick={() => { if (isLocked) setShowUpgradeModal(true); }}
          />
        </div>
        <svg
          className={`w-4 h-4 text-violet-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="mt-2 border border-violet-200 rounded-xl bg-white overflow-hidden">
          {/* S6 — Locked empty state */}
          {isLocked ? (
            <LockedFeatureEmptyState
              featureLabel="AI Scribe"
              description="Generate SOAP notes automatically from your consultation audio."
              isAdmin={isAdmin}
              variant={gateState === "expired" ? "expired" : "locked"}
              expiredDate={aiScribeFlag?.expires_at}
              onUpgradeClick={handleUpgradeClick}
              onNotifyAdmin={handleNotifyAdmin}
            />
          ) : (
          <div className="p-4">
          {/* Trial active banner inside panel */}
          {gateState === "trial" && aiScribeFlag?.days_left !== null && aiScribeFlag?.days_left !== undefined && (
            <div className="-mx-4 -mt-4 mb-4">
              <TrialActiveBanner featureLabel="AI Scribe" daysLeft={aiScribeFlag.days_left} />
            </div>
          )}

          {/* ── Error banner ─────────────────────────────────────────── */}
          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* ── Controls: idle / recording ────────────────────────── */}
          {(status === "idle" || status === "recording" || status === "error") && (
            <div className="flex flex-col items-center gap-4">
              {/* File upload */}
              {!isRecording && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-violet-200 rounded-xl p-4 text-center cursor-pointer hover:bg-violet-50 transition"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED}
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <p className="text-sm font-medium text-slate-700">
                    Upload audio file
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    MP3, WAV, M4A, WebM — up to 200 MB
                  </p>
                </div>
              )}

              {!isRecording && (
                <div className="flex items-center gap-3 w-full">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400">OR record live</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              )}

              {/* Record button */}
              <div className="flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={handleRecordToggle}
                  disabled={false}
                  className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg ${
                    isRecording
                      ? "bg-red-500 hover:bg-red-600 text-white scale-110 animate-pulse"
                      : "bg-violet-700 hover:bg-violet-800 text-white"
                  }`}
                >
                  {isRecording ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="6" width="12" height="12" rx="2" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                    </svg>
                  )}
                </button>
                <span
                  className={`text-xs font-mono ${isRecording ? "text-red-600 font-semibold" : "text-slate-400"}`}
                >
                  {isRecording ? formatTime(duration) : "Tap to record"}
                </span>
              </div>
            </div>
          )}

          {/* ── Processing state ──────────────────────────────────── */}
          {status === "processing" && (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="w-10 h-10 border-3 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
              <p className="text-sm font-medium text-violet-700">
                Processing with Gemini AI...
              </p>
              <p className="text-xs text-slate-400">
                Transcribing audio, extracting SOAP note &amp; prescription
              </p>
            </div>
          )}

          {/* ── Results ───────────────────────────────────────────── */}
          {status === "done" && result && (
            <div className="space-y-4">
              {/* Meta */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-green-600 text-lg">&#10003;</span>
                  <span className="text-sm font-medium text-green-700">
                    AI Scribe complete
                  </span>
                  <span className="text-xs text-slate-400">
                    {result.meta.processing_time_s}s /{" "}
                    {result.meta.audio_size_mb} MB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="text-xs text-violet-600 hover:text-violet-800 underline"
                >
                  Record again
                </button>
              </div>

              {/* SOAP Note summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                  SOAP Note (auto-filled above)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700">
                  {result.soap_note.chief_complaint && (
                    <div>
                      <span className="font-medium text-slate-500">CC:</span>{" "}
                      {result.soap_note.chief_complaint}
                    </div>
                  )}
                  {result.soap_note.assessment && (
                    <div>
                      <span className="font-medium text-slate-500">Dx:</span>{" "}
                      {result.soap_note.assessment}
                    </div>
                  )}
                </div>
              </div>

              {/* Prescription — medications table */}
              {result.prescription_document.medications.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-violet-700 uppercase tracking-wide">
                      Prescription ({result.prescription_document.medications.length} medications)
                    </h4>
                    {result.prescription_document.diagnosis_label && (
                      <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                        {result.prescription_document.diagnosis_label}
                      </span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-violet-200 text-left">
                          <th className="py-1.5 pr-2 text-slate-600">Drug</th>
                          <th className="py-1.5 pr-2 text-slate-600">Dose</th>
                          <th className="py-1.5 pr-2 text-slate-600">Freq</th>
                          <th className="py-1.5 pr-2 text-slate-600">
                            Timing
                          </th>
                          <th className="py-1.5 pr-2 text-slate-600">Days</th>
                          <th className="py-1.5 pr-2 text-slate-600">Qty</th>
                          <th className="py-1.5 text-slate-600">Alert</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.prescription_document.medications.map(
                          (med: AiScribeMedication, i: number) => (
                            <tr
                              key={i}
                              className={`border-b border-violet-100 ${
                                med.allergy_conflict ? "bg-red-50" : ""
                              }`}
                            >
                              <td className="py-1.5 pr-2 font-medium">
                                {med.drug_name}
                              </td>
                              <td className="py-1.5 pr-2">{med.dosage}</td>
                              <td className="py-1.5 pr-2">
                                <span
                                  className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700"
                                  title={FREQ_LABEL[med.frequency] || ""}
                                >
                                  {med.frequency}
                                </span>
                              </td>
                              <td className="py-1.5 pr-2">
                                {med.timing || "—"}
                              </td>
                              <td className="py-1.5 pr-2">
                                {med.duration_days}
                              </td>
                              <td className="py-1.5 pr-2">
                                {med.total_quantity}
                              </td>
                              <td className="py-1.5">
                                {med.allergy_conflict && (
                                  <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                                    ALLERGY
                                  </span>
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Investigations */}
                  {result.prescription_document.investigations?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-violet-200">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase">
                        Investigations:
                      </span>
                      <span className="text-xs text-slate-700 ml-1">
                        {result.prescription_document.investigations.join(
                          ", "
                        )}
                      </span>
                    </div>
                  )}

                  {/* Advice */}
                  {result.prescription_document.advice && (
                    <div className="mt-1">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase">
                        Advice:
                      </span>
                      <span className="text-xs text-slate-700 ml-1">
                        {result.prescription_document.advice}
                      </span>
                    </div>
                  )}

                  {/* Follow-up */}
                  {result.prescription_document.follow_up && (
                    <div className="mt-1">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase">
                        Follow-up:
                      </span>
                      <span className="text-xs text-slate-700 ml-1">
                        {result.prescription_document.follow_up}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
          )}
        </div>
      )}

      {/* S4 — Upgrade Prompt Modal */}
      {showUpgradeModal && (
        <UpgradePromptModal
          feature="ai_scribe"
          featureLabel="AI Scribe"
          variant={gateState === "expired" ? (aiScribeFlag?.is_trial ? "trial_expired" : "subscription_expired") : "feature_not_enabled"}
          expiredDate={aiScribeFlag?.expires_at}
          isAdmin={isAdmin}
          onDismiss={() => setShowUpgradeModal(false)}
          onUpgradeClick={handleUpgradeClick}
          onNotifyAdmin={handleNotifyAdmin}
        />
      )}
    </div>
  );
};

export default AIScribePanel;
